import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import MemberForm from "@/pages/MemberForm";
import VisitingCard from "@/pages/VisitingCard";
import AdminLogin from "@/pages/AdminLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminMemberProfile from "@/pages/AdminMemberProfile";
import AdminPlatform from "@/pages/AdminPlatform";
import AdminYearView from "@/pages/AdminYearView";
import NameReview from "@/pages/NameReview";
import PhotoDashboard from "@/pages/PhotoDashboard";
import PhotoLibrary from "@/pages/PhotoLibrary";
import ClassificationBackfill from "@/pages/ClassificationBackfill";
import PositionsManager from "@/pages/PositionsManager";
import MasterClubs from "@/pages/MasterClubs";
import LandingPage from "@/pages/LandingPage";
import NotFound from "@/pages/NotFound";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

function RequireAdmin({ children }) {
    const { user } = useAuth();
    if (user === null) {
        return (
            <div className="min-h-screen grid place-items-center text-muted-foreground text-sm">
                Loading…
            </div>
        );
    }
    if (!user) return <Navigate to="/admin/login" replace />;
    // District admins are locked to their year workspace
    if (user.role === "district_admin" && user.year_id) {
        const path = window.location.pathname;
        const okPath = path.startsWith(`/admin/year/${user.year_id}`);
        if (!okPath) return <Navigate to={`/admin/year/${user.year_id}`} replace />;
    }
    // Super admins land on the global platform dashboard, not the legacy single-district view
    if (user.role === "super_admin") {
        const path = window.location.pathname;
        if (path === "/admin" || path === "/admin/") {
            return <Navigate to="/admin/platform" replace />;
        }
    }
    return children;
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/form/:token" element={<MemberForm />} />
                        <Route path="/card/:token" element={<VisitingCard />} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin/forgot-password" element={<ForgotPassword />} />
                        <Route path="/admin/reset-password/:token" element={<ResetPassword />} />
                        <Route
                            path="/admin"
                            element={
                                <RequireAdmin>
                                    <AdminDashboard />
                                </RequireAdmin>
                            }
                        />
                        <Route
                            path="/admin/members/:token"
                            element={
                                <RequireAdmin>
                                    <AdminMemberProfile />
                                </RequireAdmin>
                            }
                        />
                        <Route
                            path="/admin/platform"
                            element={
                                <RequireAdmin>
                                    <AdminPlatform />
                                </RequireAdmin>
                            }
                        />
                        <Route
                            path="/admin/year/:yid"
                            element={
                                <RequireAdmin>
                                    <AdminYearView />
                                </RequireAdmin>
                            }
                        />
                        <Route
                            path="/admin/names"
                            element={
                                <RequireAdmin>
                                    <NameReview />
                                </RequireAdmin>
                            }
                        />
                        <Route
                            path="/admin/photos"
                            element={
                                <RequireAdmin>
                                    <PhotoDashboard />
                                </RequireAdmin>
                            }
                        />
                        <Route
                            path="/admin/photos/library"
                            element={
                                <RequireAdmin>
                                    <PhotoLibrary />
                                </RequireAdmin>
                            }
                        />
                        <Route
                            path="/admin/classification-backfill"
                            element={
                                <RequireAdmin>
                                    <ClassificationBackfill />
                                </RequireAdmin>
                            }
                        />
                        <Route
                            path="/admin/positions"
                            element={
                                <RequireAdmin>
                                    <PositionsManager />
                                </RequireAdmin>
                            }
                        />
                        <Route
                            path="/admin/clubs"
                            element={
                                <RequireAdmin>
                                    <MasterClubs />
                                </RequireAdmin>
                            }
                        />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
            <Toaster position="top-right" richColors />
        </div>
    );
}

export default App;
